import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import {
  ExpandMore,
  Save,
  ContentCopy,
  Refresh,
  Edit,
  Undo,
  Security,
  Gavel,
  Shield,
} from '@mui/icons-material';
import { deepResearchAPI } from '../services/api';

interface Prompt {
  id: number;
  prompt_type: string;
  template: string;
  created_by: string;
  created_at: string;
  is_active: boolean;
}

interface PromptSection {
  type: 'defense' | 'prosecution' | 'judge';
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface NotificationState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const DeepResearchPrompts: React.FC = () => {
  const [prompts, setPrompts] = useState<Record<string, Prompt>>({});
  const [editedPrompts, setEditedPrompts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [notification, setNotification] = useState<NotificationState>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [expandedPanel, setExpandedPanel] = useState<string>('defense');

  const promptSections: PromptSection[] = [
    {
      type: 'defense',
      title: 'Defense Advocate',
      description: 'Prompt for generating defensive arguments and explanations for procurement decisions',
      icon: <Shield />,
      color: '#4CAF50'
    },
    {
      type: 'prosecution',
      title: 'Prosecution Analyst',
      description: 'Prompt for identifying and articulating potential fraud indicators and concerns',
      icon: <Security />,
      color: '#F44336'
    },
    {
      type: 'judge',
      title: 'Impartial Judge',
      description: 'Prompt for providing balanced assessment and final judgment on procurement practices',
      icon: <Gavel />,
      color: '#2196F3'
    }
  ];

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const response = await deepResearchAPI.getPrompts();
      const promptsMap: Record<string, Prompt> = {};
      const editedMap: Record<string, string> = {};
      
      response.prompts.forEach((prompt: Prompt) => {
        promptsMap[prompt.prompt_type] = prompt;
        editedMap[prompt.prompt_type] = prompt.template;
      });
      
      setPrompts(promptsMap);
      setEditedPrompts(editedMap);
    } catch (error) {
      console.error('Error fetching prompts:', error);
      showNotification('Failed to fetch prompts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePromptChange = (promptType: string, value: string) => {
    setEditedPrompts(prev => ({
      ...prev,
      [promptType]: value
    }));
  };

  const handleSavePrompt = async (promptType: string) => {
    try {
      setSaving(prev => ({ ...prev, [promptType]: true }));
      
      await deepResearchAPI.updatePrompt(promptType, {
        template: editedPrompts[promptType],
        created_by: 'admin'
      });

      // Refresh the specific prompt
      const response = await deepResearchAPI.getPrompts();
      const updatedPrompt = response.prompts.find((p: Prompt) => p.prompt_type === promptType);
      if (updatedPrompt) {
        setPrompts(prev => ({
          ...prev,
          [promptType]: updatedPrompt
        }));
      }

      showNotification(`${promptType} prompt updated successfully!`, 'success');
    } catch (error) {
      console.error('Error saving prompt:', error);
      showNotification(`Failed to save ${promptType} prompt`, 'error');
    } finally {
      setSaving(prev => ({ ...prev, [promptType]: false }));
    }
  };

  const handleResetPrompt = (promptType: string) => {
    const originalPrompt = prompts[promptType]?.template || '';
    setEditedPrompts(prev => ({
      ...prev,
      [promptType]: originalPrompt
    }));
    showNotification(`${promptType} prompt reset to original`, 'info');
  };

  const handleCopyToClipboard = async (promptType: string) => {
    try {
      await navigator.clipboard.writeText(editedPrompts[promptType] || '');
      showNotification(`${promptType} prompt copied to clipboard`, 'success');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      showNotification('Failed to copy to clipboard', 'error');
    }
  };

  const showNotification = (message: string, severity: NotificationState['severity']) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : '');
  };

  const isPromptChanged = (promptType: string) => {
    return editedPrompts[promptType] !== prompts[promptType]?.template;
  };

  const renderPromptSection = (section: PromptSection) => {
    const promptData = prompts[section.type];
    const isChanged = isPromptChanged(section.type);
    const isSaving = saving[section.type];

    return (
      <Accordion
        key={section.type}
        expanded={expandedPanel === section.type}
        onChange={handleAccordionChange(section.type)}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2} width="100%">
            <Box sx={{ color: section.color }}>
              {section.icon}
            </Box>
            <Box flex={1}>
              <Typography variant="h6" component="div">
                {section.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {section.description}
              </Typography>
            </Box>
            {isChanged && (
              <Chip
                label="Modified"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
            {promptData && (
              <Chip
                label={`Last updated: ${new Date(promptData.created_at).toLocaleDateString()}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <TextField
              fullWidth
              multiline
              rows={20}
              variant="outlined"
              value={editedPrompts[section.type] || ''}
              onChange={(e) => handlePromptChange(section.type, e.target.value)}
              placeholder={`Enter ${section.title} prompt template...`}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  lineHeight: 1.4,
                },
              }}
            />
            
            <Divider sx={{ my: 2 }} />
            
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={16} /> : <Save />}
                onClick={() => handleSavePrompt(section.type)}
                disabled={isSaving || !isChanged}
                color="primary"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<Undo />}
                onClick={() => handleResetPrompt(section.type)}
                disabled={!isChanged}
              >
                Reset
              </Button>
              
              <Tooltip title="Copy to clipboard">
                <IconButton
                  onClick={() => handleCopyToClipboard(section.type)}
                  color="default"
                >
                  <ContentCopy />
                </IconButton>
              </Tooltip>
              
              <Box flex={1} />
              
              <Typography variant="body2" color="text.secondary">
                {editedPrompts[section.type]?.length || 0} characters
              </Typography>
            </Stack>
            
            {promptData && (
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Created by: {promptData.created_by} | 
                  Status: {promptData.is_active ? 'Active' : 'Inactive'} | 
                  ID: {promptData.id}
                </Typography>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  if (loading) {
    return (
      <Box p={3}>
        <Typography variant="h4" gutterBottom>
          Deep Research Prompt Editor
        </Typography>
        <Card>
          <CardContent>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Loading prompts...
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Deep Research Prompt Editor
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Configure the AI prompts used in deep research analysis. Each role provides a different perspective 
          on procurement decisions and potential fraud indicators.
        </Typography>
        
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchPrompts}
            disabled={loading}
          >
            Refresh Prompts
          </Button>
          
          <Chip
            icon={<Edit />}
            label={`${Object.keys(editedPrompts).filter(key => isPromptChanged(key)).length} modified`}
            color={Object.keys(editedPrompts).some(key => isPromptChanged(key)) ? 'warning' : 'default'}
            variant="outlined"
          />
        </Stack>
        
        <Divider />
      </Box>

      {/* Prompt Sections */}
      <Box>
        {promptSections.map(section => renderPromptSection(section))}
      </Box>

      {/* Global Actions */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Batch Operations
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => {
                promptSections.forEach(section => {
                  if (isPromptChanged(section.type)) {
                    handleResetPrompt(section.type);
                  }
                });
              }}
              disabled={!Object.keys(editedPrompts).some(key => isPromptChanged(key))}
            >
              Reset All Changes
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                for (const section of promptSections) {
                  if (isPromptChanged(section.type)) {
                    await handleSavePrompt(section.type);
                  }
                }
              }}
              disabled={!Object.keys(editedPrompts).some(key => isPromptChanged(key))}
            >
              Save All Changes
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeepResearchPrompts;